import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Header } from './Header/Header';
import { Footer } from './Footer/Footer';
import { ProponiCorrezione } from './ProponiCorrezione/ProponiCorrezione';
import { A11yPanel } from '@/a11y/A11yPanel';
import { KioskOverlay } from '@/kiosk/KioskOverlay';
import { KioskProvider } from '@/kiosk/KioskProvider';
import styles from './AppLayout.module.css';

const MAIN_ID = 'contenuto-principale';

/**
 * Guscio dell'applicazione.
 *
 * Il problema di accessibilità numero uno di una SPA (e la decisione 1 è
 * "SPA React pura") è che **la navigazione client-side non viene annunciata**:
 * il browser non ricarica la pagina, quindi lo screen reader non dice nulla e
 * il focus resta dov'era, spesso su un link della pagina precedente. È una
 * violazione di WCAG 2.4.3 e 4.1.3 che il portale statico non aveva, perché
 * ogni pagina era un documento nuovo.
 *
 * Rimedio implementato qui, in tre pezzi:
 *  1. `<title>` aggiornato a ogni rotta (lo fa `useDocumentTitle` nelle pagine);
 *  2. una **regione live** che annuncia il titolo della nuova pagina;
 *  3. il **focus spostato sull'`<h1>`** (o su `<main>`) dopo la navigazione,
 *     così il lettore riprende dall'inizio del nuovo contenuto.
 *
 * Il focus NON viene spostato al primo montaggio: al caricamento iniziale il
 * browser è già in cima e uno spostamento sarebbe un salto inatteso.
 */
/**
 * `KioskProvider` usa `useLocation`/`useNavigate`: deve stare DENTRO il router.
 * Il layout è il primo punto disponibile, quindi lo monta qui.
 */
export function AppLayout() {
  return (
    <KioskProvider>
      <AppShell />
    </KioskProvider>
  );
}

function AppShell() {
  const { t } = useTranslation();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const isFirstRender = useRef(true);
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // 2. Annuncio. Si legge dal DOM invece di duplicare il titolo: la fonte
    //    è l'h1 reale, quindi non può divergere da quello che si vede.
    const heading = mainRef.current?.querySelector('h1');
    const title = heading?.textContent?.trim() || document.title;
    setAnnouncement(title);

    // 3. Focus. `tabIndex={-1}` è già sull'h1/main, quindi è focalizzabile
    //    programmaticamente senza entrare nella sequenza di Tab.
    const target = (heading ?? mainRef.current) as HTMLElement | null;
    if (target) {
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: false });
    }

    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [location.pathname, location.search]);

  return (
    <div className={styles.shell}>
      {/* Primo elemento focalizzabile della pagina (WCAG 2.4.1). */}
      <a href={`#${MAIN_ID}`} className={styles.skipLink}>
        {t('skip_to_content', 'Salta al contenuto')}
      </a>

      <Header />

      <main id={MAIN_ID} ref={mainRef} className={styles.main}>
        <Outlet />
      </main>

      <Footer />

      {/* Flottante del riferimento: sul portale pubblico, non in /redazione/
          (questa shell copre solo le rotte pubbliche). */}
      <ProponiCorrezione />

      {/* `aria-live="polite"` + `role="status"`: annuncia senza interrompere.
          Non `assertive`: un cambio pagina non è un'emergenza. */}
      <div className={styles.announcer} role="status" aria-live="polite" aria-atomic="true">
        {announcement}
      </div>

      <A11yPanel />
      <KioskOverlay />
    </div>
  );
}
